import { ArrowRight, BarChart2, FileAudio, FileCode, FileText, Image } from 'lucide-react'
import type { WorkSubcategoryInfo } from '../../types/work'

interface WorkSubcategoryCardProps {
  subcategory: WorkSubcategoryInfo
  selected: boolean
  onSelect: () => void
}

const IO_ICONS: Record<string, React.ReactNode> = {
  text: <FileText size={12} />,
  file: <FileAudio size={12} />,
  both: <FileAudio size={12} />,
  json: <BarChart2 size={12} />,
  image: <Image size={12} />,
  code: <FileCode size={12} />,
}

export default function WorkSubcategoryCard({ subcategory, selected, onSelect }: WorkSubcategoryCardProps) {
  const unitLabel = subcategory.pricing.unit.replace(/_/g, ' ')

  return (
    <div
      onClick={onSelect}
      style={{
        padding: '14px 16px',
        borderRadius: '12px',
        background: selected ? 'rgba(99, 102, 241, 0.08)' : 'var(--card-bg)',
        border: `1px solid ${selected ? 'var(--primary, #6366f1)' : 'var(--border-color)'}`,
        cursor: 'pointer',
        transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        boxShadow: selected ? '0 0 0 1px var(--primary, #6366f1)' : 'none',
      }}
      onMouseEnter={e => {
        if (!selected) {
          (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(99, 102, 241, 0.5)'
        }
      }}
      onMouseLeave={e => {
        if (!selected) {
          (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-color)'
        }
      }}
    >
      {/* Title + model badge */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px' }}>
        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)' }}>
          {subcategory.label}
        </div>
        <span
          style={{
            flexShrink: 0,
            fontSize: '10px',
            padding: '2px 7px',
            borderRadius: '20px',
            background: 'rgba(107, 114, 128, 0.12)',
            color: 'var(--text-muted, #6b7280)',
            fontFamily: 'monospace',
          }}
        >
          {subcategory.model.split('/').pop()}
        </span>
      </div>

      {/* Description */}
      <div
        style={{
          fontSize: '12px',
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {subcategory.description}
      </div>

      {/* Example use */}
      <div
        style={{
          fontSize: '11px',
          color: 'var(--text-muted, #6b7280)',
          fontStyle: 'italic',
          lineHeight: 1.4,
        }}
      >
        {subcategory.example_use}
      </div>

      {/* Footer: io type + price */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '2px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '11px',
            color: 'var(--text-muted, #6b7280)',
          }}
        >
          {IO_ICONS[subcategory.input_type] ?? <FileText size={12} />}
          <ArrowRight size={10} />
          {IO_ICONS[subcategory.output_type] ?? <FileText size={12} />}
          <span style={{ marginLeft: '4px' }}>
            {subcategory.input_type} → {subcategory.output_type}
          </span>
        </div>
        <span
          style={{
            fontSize: '12px',
            fontWeight: 700,
            color: 'var(--primary, #6366f1)',
          }}
        >
          {subcategory.pricing.amount} FLZ/{unitLabel}
        </span>
      </div>
    </div>
  )
}
